import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search } from 'lucide-react';

// Fix Leaflet default icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface AddressPickerProps {
    initialAddress?: string;
    initialLat?: number;
    initialLng?: number;
    onSelect: (address: string, lat: number, lng: number) => void;
}

const LocationMarker = ({ position, setPosition }: { position: L.LatLng | null, setPosition: (pos: L.LatLng) => void }) => {
    const map = useMapEvents({
        click(e) {
            setPosition(e.latlng);
            map.flyTo(e.latlng, map.getZoom());
        },
    });

    useEffect(() => {
        if (position) {
            map.flyTo(position, map.getZoom());
        }
    }, [position, map]);

    return position === null ? null : (
        <Marker position={position}></Marker>
    );
}

const AddressPicker = ({ initialAddress, initialLat, initialLng, onSelect }: AddressPickerProps) => {
    const [searchTerm, setSearchTerm] = useState(initialAddress || '');
    const [position, setPosition] = useState<L.LatLng | null>(
        initialLat != null && initialLng != null ? new L.LatLng(initialLat, initialLng) : null
    );
    const [results, setResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    // Default center (Ubatuba, SP as fallback)
    const defaultCenter = { lat: -23.4332, lng: -45.0834 };

    // Update internal state if props change
    useEffect(() => {
        if (initialLat != null && initialLng != null) {
            setPosition(new L.LatLng(initialLat, initialLng));
        }
        if (initialAddress != null && initialAddress !== '') {
            setSearchTerm(initialAddress);
        }
    }, [initialLat, initialLng, initialAddress]);

    const handleSearch = async () => {
        if (!searchTerm) return;
        setSearching(true);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=5&addressdetails=1`, {
                headers: {
                    'User-Agent': 'TuristaApp/1.0'
                }
            });
            const data = await response.json();
            setResults(data);
        } catch (error) {
            console.error("Search error", error);
        } finally {
            setSearching(false);
        }
    };

    const selectResult = (item: any) => {
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.lon);
        const newPos = new L.LatLng(lat, lng);
        setPosition(newPos);
        setResults([]);

        // Construct a nice display address
        const addr = item.address;
        const displayAddress = [
            addr.road || addr.pedestrian || addr.footway,
            addr.house_number,
            addr.suburb || addr.neighbourhood,
            addr.city || addr.town || addr.village,
            addr.state
        ].filter(Boolean).join(', ');

        setSearchTerm(displayAddress || item.display_name);
        onSelect(displayAddress || item.display_name, lat, lng);
    };

    return (
        <div className="address-picker">
            <div className="search-box" style={{ marginBottom: '1rem', position: 'relative' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar endereço..."
                        style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                        type="button"
                        onClick={handleSearch}
                        className="search-btn"
                        style={{ padding: '0.5rem', borderRadius: '4px', background: '#3b82f6', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        {searching ? '...' : <Search size={20} />}
                    </button>
                </div>

                {results.length > 0 && (
                    <div className="search-results" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        zIndex: 1000,
                        border: '1px solid #ccc',
                        borderRadius: '0 0 4px 4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}>
                        {results.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => selectResult(item)}
                                style={{ padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid #eee', color: '#333' }}
                                className="hover:bg-gray-100"
                            >
                                {item.display_name}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ccc' }}>
                <MapContainer
                    center={position || defaultCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker
                        position={position}
                        setPosition={(pos) => {
                            setPosition(pos);
                            // Reverse geocode could go here to get address from pin drop, but for now just getting coords
                            onSelect(searchTerm, pos.lat, pos.lng);
                        }}
                    />
                </MapContainer>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                Clique no mapa para ajustar a localização exata.
            </p>
        </div>
    );
};

export default AddressPicker;
